import { Component, Inject, ChangeDetectionStrategy } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { DefaultSnackBarService } from '../../../services/default-snack-bar.service';
import { DocumentTrackingService } from '../document-tracking.service';
import { FormControl, FormGroup } from '@angular/forms';
import DocType from '../../../interfaces/doc-type';
import DocSubtype from '../../../interfaces/doc-subtype';
import { combineLatest, debounceTime, Subscription } from 'rxjs';
import Fund from '../../../interfaces/fund';
import Project from '../../../interfaces/project';
import OwnedEntity from '../../../interfaces/owned-entity';
import ID from '../../../types/id';
import DocStatus from '../../../interfaces/doc-status';
import { AddDocumentDialogService } from './add-document-dialog.service';
import VOwnedEntitiesMap from '../../../interfaces/v-owned-entities-map';
import entity from '../../../interfaces/entity';
import { CurrentUserService } from '../../../services/current-user.service';
import { formatDate } from '@angular/common';
import { CreateDocumentInput } from '../graphql/document-tracking';
type DialogData = {};
interface Nameable {
  id: ID;
  name: string;
}
@Component({
    selector: 'app-add-document-dialog',
    templateUrl: './add-document-dialog.component.html',
    styleUrls: ['./add-document-dialog.component.scss'],
    providers: [AddDocumentDialogService],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class AddDocumentDialogComponent {
  formControls = new FormGroup({
    entity_type: new FormControl<string | null>(null),
    entity_name: new FormControl<string | VOwnedEntitiesMap | null>(null),
    period: new FormControl<string | Date | null>(null),
    file_type: new FormControl<DocType | null>(null),
    sub_type: new FormControl<DocSubtype | null>(null),
    doc_status_id: new FormControl<ID | null>(null),
  });
  subTypes: DocSubtype[] = [];
  typeSub: Subscription;
  nameSub: Subscription;
  statuses: DocStatus[] = [];
  constructor(
    public dialogRef: MatDialogRef<AddDocumentDialogComponent>,
    public documentTrackerService: DocumentTrackingService,
    private _snackBar: DefaultSnackBarService,
    public documentDialogService: AddDocumentDialogService,
    private userService: CurrentUserService,
    @Inject(MAT_DIALOG_DATA) public data: DialogData
  ) {}
  ngOnInit() {
    this.typeSub = this.formControls.valueChanges.subscribe(
      ({ file_type, sub_type }) => {
        if (file_type?.docSubtypes) {
          this.subTypes = file_type.docSubtypes
            .slice()
            .sort((a, b) => a.sort_order - b.sort_order);
        }
        if (sub_type && file_type?.docTypeSubtypeStatuses) {
          this.statuses = file_type.docTypeSubtypeStatuses
            .filter((status) => status.doc_subtype_id === sub_type.id)
            .sort((a, b) => a.sort_order - b.sort_order)
            .map((status) => status.docStatus);
        }
      }
    );
    this.nameSub = this.formControls.controls.entity_name.valueChanges
      .pipe(debounceTime(500))
      .subscribe((value) => {
        if (
          typeof value === 'string' &&
          this.formControls.controls.entity_type.value
        ) {
          this.documentDialogService.searchEntities(
            this.formControls.controls.entity_type.value,
            value
          );
        } else if (value === null) {
          this.documentDialogService.clearOptions();
        }
      });
  }
  displayFn(entityMap: VOwnedEntitiesMap) {
    if (entityMap) {
      let relatedString = '';
      let entityString = '';
      if (entityMap.related_name?.length) {
        relatedString = ` [${entityMap.related_name}]`;
      }
      if (entityMap.owned_entity_name?.length) {
        entityString = `${entityMap.owned_entity_name}`;
      } else {
        entityString = 'Unknown Entity';
      }
      return `${entityString}${relatedString}`;
    }
    return '';
  }
  public save() {
    if (this.formControls.valid) {
      let data = this.formControls.value;
      if (data.entity_name && typeof data.entity_name !== 'string') {
        let entity = data.entity_name as VOwnedEntitiesMap;
        let sub: Subscription;
        sub = this.userService.user$.subscribe((user) => {
          let input: CreateDocumentInput = {
            current_doc_status_id: data.doc_status_id ?? '',
            current_doc_subtype_id: data.sub_type?.id ?? '',
            doc_type_id: data.file_type?.id ?? '',
            owned_entity_id: entity.owned_entity_id,
            user_id: user?.id ?? '',
            doc_date: formatDate(
              data.period ?? new Date(),
              'yyyy-MM-dd',
              'en-US'
            ),
            current_version: 1,
            current_status_date: formatDate(new Date(), 'yyyy-MM-dd', 'en-US'),
          };
          let docSub: Subscription;
          docSub = this.documentTrackerService
            .createDocument(input)
            .subscribe(({ data, loading }) => {
              this._snackBar.open('Successfully created document');
              this.documentTrackerService.refreshDocuments();
              this.dialogRef.close();
              docSub?.unsubscribe();
            });
          sub?.unsubscribe();
        });
      }
    }
  }
}
